import { Injectable, inject, computed } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { User } from 'app/core/user/user.types';
import { Observable, map } from 'rxjs';
import { AuthState } from '../state/auth.state';

@Injectable({ providedIn: 'root' })
export class UserService {
    private _authState = inject(AuthState);
    
    private _defaultUser: User = {
        id: '',
        name: 'Guest',
        email: '',
        avatar: 'images/avatars/default.jpg',
        status: 'online'
    };

    /**
     * Computed signal that maps AppUser to Fuse's User type
     */
    private _userSignal = computed<User>(() => {
        const appUser = this._authState.user();
        if (!appUser) {
            return this._defaultUser;
        }
        return {
            id: appUser.id,
            name: appUser.name ?? `${appUser.firstName} ${appUser.lastName}`,
            email: appUser.emailAddress,
            avatar: appUser.imageURL || 'images/avatars/default.jpg',
            status: 'online'
        };
    });

    /**
     * Observable for components that need RxJS compatibility
     */
    readonly user$: Observable<User> = toObservable(this._userSignal);

    /**
     * Direct getter for synchronous access
     */
    get user(): User {
        return this._userSignal();
    }

    /**
     * Setter - delegates to AuthState
     */
    set user(value: User | null) {
        // If Fuse components try to set user directly, 
        // we could update AuthState, but typically auth flow handles this
        console.warn('UserService.user setter called - auth should be managed via AuthState');
    }

    /**
     * Update delegates to AuthState
     */
    update(user: Partial<User>): Observable<User> {
        // Map Fuse User fields back to AppUser fields
        this._authState.updateUser({
            name: user.name,
            emailAddress: user.email,
            imageURL: user.avatar
        });
        
        return this.user$;
    }

    /**
     * Clear handled by AuthState
     */
    clearUser(): void {
        // AuthState.clearAuth() handles this
        console.log('UserService.clearUser - delegating to AuthState');
    }
}

// OLD
// import { HttpClient } from '@angular/common/http';
// import { inject, Injectable } from '@angular/core';
// import { User } from 'app/core/user/user.types';
// import { BehaviorSubject, Observable, map, tap } from 'rxjs';
// import { environment } from 'environments/environment';

// @Injectable({ providedIn: 'root' })
// export class UserService {
//     private _httpClient = inject(HttpClient);
    
//     // Initialize with default user to prevent undefined errors in layouts
//     private _defaultUser: User = {
//         id: '',
//         name: 'Guest',
//         email: '',
//         avatar: 'images/avatars/default.jpg',
//         status: 'online'
//     };
    
//     // Start with default user instead of null
//     private _user: BehaviorSubject<User> = new BehaviorSubject<User>(this._defaultUser);

//     /**
//      * Expose user$ - always has a valid User object
//      */
//     get user$(): Observable<User> {
//         return this._user.asObservable();
//     }

//     /**
//      * Direct setter - ensures avatar is never null
//      */
//     set user(value: User | null) {
//         if (value) {
//             // Ensure avatar is never null/undefined
//             if (!value.avatar) {
//                 value.avatar = 'images/avatars/default.jpg';
//             }
//             this._user.next(value);
//         } else {
//             // If setting to null, use default user instead
//             this._user.next(this._defaultUser);
//         }
//     }

//     /**
//      * Direct getter
//      */
//     get user(): User {
//         return this._user.value;
//     }

//     /**
//      * Update user
//      *
//      * @param user
//      */
//     update(user: Partial<User>): Observable<User> {
//         // Update the local user object first
//         const currentUser = this._user.value;
//         const updatedUser = { ...currentUser, ...user };
        
//         // Ensure avatar is never null
//         if (!updatedUser.avatar) {
//             updatedUser.avatar = 'images/avatars/default.jpg';
//         }
        
//         // Update the behavior subject
//         this._user.next(updatedUser);

//         // If you want to sync with backend, uncomment this:
//         // return this._httpClient.patch<User>(`${environment.apiUrl}/users/${updatedUser.id}`, user).pipe(
//         //     tap((response) => {
//         //         // Ensure avatar is set in response
//         //         if (!response.avatar) {
//         //             response.avatar = 'images/avatars/default.jpg';
//         //         }
//         //         this._user.next(response);
//         //     })
//         // );

//         // For now, just return the updated user
//         return new Observable(observer => {
//             observer.next(updatedUser);
//             observer.complete();
//         });
//     }

//     /**
//      * Clear user on sign out - revert to default
//      */
//     clearUser(): void {
//         this._user.next(this._defaultUser);
//     }
// }